import { useState } from "react";
import { USER_ROLES } from "../../api/users";
import { Field, PanelMessage, SelectField, Spinner, formatDate } from "./shared";
import styles from "./Admin.module.css";

/*
  Team: the people who can sign in to this control panel. Admins invite new
  members by email (they set their own password from the link they get);
  admins can still change an existing member's role, reset their password,
  disable or remove them. Members see everything else but not this tab.
*/
export default function UsersPanel({ users, invites, me }) {
  const [inviting, setInviting] = useState(false);

  if (users.error && !users.items) {
    return (
      <PanelMessage>
        {users.error} <button className={styles.linkBtn} onClick={users.load}>Retry</button>
      </PanelMessage>
    );
  }
  if (users.items === null) return <PanelMessage><Spinner /></PanelMessage>;

  const isMe = (u) => String(u.id) === String(me.uid);

  return (
    <div className={styles.dash}>
      {inviting && (
        <InviteForm
          onCancel={() => setInviting(false)}
          onSave={async (data) => {
            await invites.add(data);
            setInviting(false);
          }}
        />
      )}

      <div className={styles.toolbar}>
        <p className={styles.sub} style={{ margin: 0 }}>
          Invite people and enable or disable their access. Members edit their own name and password from My account.
        </p>
        <div className={styles.headActions}>
          <button className={styles.ghostBtn} onClick={users.load}>Refresh</button>
          <button className={styles.solidBtn} onClick={() => setInviting(true)}>+ Invite member</button>
        </div>
      </div>

      {users.error && <p className={styles.error}>{users.error}</p>}

      <div className={styles.memberGrid}>
        {users.items.map((u) => {
          const self = isMe(u);
          const active = u.active !== false;
          return (
            <article key={u.id} className={`${styles.memberCard} ${active ? "" : styles.memberOff}`}>
              <div className={styles.memberTop}>
                <span className={styles.memberAvatar} aria-hidden="true">{initialsOf(u.name || u.email)}</span>
                <div className={styles.memberId}>
                  <strong>
                    {u.name || "—"}{self && <em> (You)</em>}
                    <small className={styles.memberRole}>{roleLabel(u.role)}</small>
                  </strong>
                  <a href={`mailto:${u.email}`}>{u.email}</a>
                </div>
              </div>
              <div className={styles.memberMeta}>
                <span className={`${styles.pill} ${active ? styles.access_on : styles.access_off}`}>
                  {active ? "Active" : "Disabled"}
                </span>
                <span className={styles.memberDate}>Added {formatDate(u.createdAt)}</span>
              </div>
              <div className={styles.memberActions}>
                {!self && (
                  <button
                    className={styles.ghostBtn}
                    onClick={() => users.update(u.id, { active: !active }).catch(() => {})}
                  >
                    {active ? "Disable" : "Enable"}
                  </button>
                )}
                {!self && (
                  <button
                    className={styles.dangerBtn}
                    onClick={() => {
                      if (window.confirm(`Remove ${u.name || u.email} from the team? They will no longer be able to sign in.`)) {
                        users.remove(u.id);
                      }
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {invites.items && invites.items.length > 0 && (
        <>
          <h2 className={styles.sectionTitle}>Pending invites</h2>
          <div className={styles.memberGrid}>
            {invites.items.map((inv) => (
              <article key={inv.id} className={`${styles.memberCard} ${styles.memberPending}`}>
                <div className={styles.memberTop}>
                  <span className={styles.memberAvatar} aria-hidden="true">{initialsOf(inv.name || inv.email)}</span>
                  <div className={styles.memberId}>
                    <strong>
                      {inv.name || "—"}
                      <small className={styles.memberRole}>{roleLabel(inv.role)}</small>
                    </strong>
                    <a href={`mailto:${inv.email}`}>{inv.email}</a>
                  </div>
                </div>
                <div className={styles.memberMeta}>
                  <span className={styles.memberDate}>Invited {formatDate(inv.createdAt)}</span>
                </div>
                <div className={styles.memberActions}>
                  <button className={styles.ghostBtn} onClick={() => invites.add(inv)}>Resend</button>
                  <button
                    className={styles.dangerBtn}
                    onClick={() => {
                      if (window.confirm(`Revoke the invite sent to ${inv.email}?`)) invites.remove(inv.id);
                    }}
                  >
                    Revoke
                  </button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function initialsOf(text) {
  const parts = String(text || "?").split(/[\s@.]+/).filter(Boolean);
  return ((parts[0]?.[0] || "?") + (parts.length > 1 ? parts[1][0] : "")).toUpperCase();
}

function roleLabel(role) {
  return USER_ROLES.find((r) => r.value === role)?.label || role;
}

function InviteForm({ onSave, onCancel }) {
  const [form, setForm] = useState({ name: "", email: "", role: "member" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const roleHint = USER_ROLES.find((r) => r.value === form.role)?.hint;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) {
      setError("Enter an email address.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onSave(form);
    } catch (err) {
      setError(err?.message || "Could not send the invite.");
      setBusy(false);
    }
  };

  return (
    <form className={styles.card} onSubmit={submit}>
      <h2>Invite a team member</h2>
      <p className={styles.sub}>They'll get an email with a link to set their own password.</p>
      <fieldset className={`${styles.group} ${styles.twoCol}`} disabled={busy}>
        <Field label="Name" value={form.name} onChange={set("name")} />
        <Field label="Email" type="email" value={form.email} onChange={set("email")} required />
        <SelectField label="Role" value={form.role} onChange={set("role")} hint={roleHint}>
          {USER_ROLES.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </SelectField>
      </fieldset>
      {error && <p className={styles.error}>{error}</p>}
      <div className={styles.formActions}>
        <button className={styles.primaryBtn} type="submit" disabled={busy}>
          {busy ? "Sending…" : "Send invite"}
        </button>
        <button className={styles.ghostBtn} type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}
